import { monitoring } from 'common/services';

import type { DeviceKind } from 'devices/core';

const KINDS: DeviceKind[] = ['microphone', 'camera'];

export type PermissionChangeTelemetryDeps = {
  getNavigator: () => Pick<Navigator, 'permissions'> | undefined;
  getDocument: () => Pick<Document, 'visibilityState' | 'hasFocus'> | undefined;
  track: (action: `devices.${DeviceKind}.permission_changed`, context: Record<string, unknown>) => void;
};

const defaultDeps: PermissionChangeTelemetryDeps = {
  getNavigator: () => (typeof navigator === 'undefined' ? undefined : navigator),
  getDocument: () => (typeof document === 'undefined' ? undefined : document),
  track: (action, context) => monitoring.trackAction(action, context),
};

/**
 * Records every change of the camera and microphone site permission while the page is open, as
 * `devices.<kind>.permission_changed` with `{ from, to, visibilityState, hasFocus }`.
 *
 * The event room has no other signal for this: when a grant lapses (Chrome's "Allow this time"
 * expires, or the user changes the site setting) and the browser asks again, a user who answers
 * Allow leaves no trace. `granted → prompt → granted` here makes that re-prompt visible.
 *
 * Returns a function that stops watching.
 */
export const watchPermissionChanges = (deps: PermissionChangeTelemetryDeps = defaultDeps): (() => void) => {
  const permissions = deps.getNavigator()?.permissions;
  if (!permissions?.query) return () => undefined;

  let stopped = false;
  const teardown: Array<() => void> = [];

  KINDS.forEach((kind) => {
    permissions
      .query({ name: kind as PermissionName })
      .then((status) => {
        if (stopped) return;
        let from = status.state;
        const onChange = () => {
          const to = status.state;
          if (to === from) return;
          const doc = deps.getDocument();
          deps.track(`devices.${kind}.permission_changed`, {
            from,
            to,
            visibilityState: doc?.visibilityState,
            hasFocus: doc?.hasFocus(),
          });
          from = to;
        };
        status.addEventListener('change', onChange);
        teardown.push(() => status.removeEventListener('change', onChange));
      })
      // Firefox has thrown for camera/microphone names; there is nothing to watch then.
      .catch(() => undefined);
  });

  return () => {
    stopped = true;
    teardown.forEach((stop) => stop());
  };
};

let started = false;

/** Start the app-wide watcher once, at boot. Later calls do nothing. */
export const startPermissionChangeTelemetry = (): void => {
  if (started) return;
  started = true;
  watchPermissionChanges();
};
